import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { ArrowLeft, ArrowRight, ArrowUp, Network } from "lucide-react";
import Separator from "../ui/Separator";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import DragNumberInput from "../ui/DragNumberInput";
import FieldLabel from "../ui/FieldLabel";
import MenuSelect from "../ui/MenuSelect";
import OffsetEditor from "./OffsetEditor";
import { useBodies, useSceneActions, useWorldBodies } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { getViewerState, getViewerActions } from "../../stores/viewerStore";
import { eulerDegreesToQuaternion, quaternionToEulerDegrees } from "../../core/transform";
import { getWorldBodyLabelForBodyUuid } from "../../utils/worldBodyLabels";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { useNavigateToObjectFromBodyUuid } from "../../hooks/useNavigateToObjectFromBodyUuid";
import { InertialVisibilityToolbar } from "../../selected-inertial-visibility";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import TransformGizmoHeaderActions from "./TransformGizmoHeaderActions";
import { useTransformGizmoControls } from "./useTransformGizmoControls";
import { computeLargestRobotDimension } from "../../viewer/computeLargestRobotDimension";
import {
  computeInertialVisualScale,
  diagonalInertiaToFullInertia,
  resolveInertialPrincipalAxes,
} from "../../utils/inertiaTensor";
import QuickNavSection from "./QuickNavSection";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import { EntireObjectIcon } from "../ui/Icons";
import Tooltip from "../ui/Tooltip";
import { usePanelStore } from "../../stores/panelStore";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";
import InfoTooltip from "../ui/InfoTooltip";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";
import { logger } from "../../utils/logger";

function parseSelectedKey(key) {
  const str = String(key || "");
  const sep = str.indexOf(":");
  if (sep > 0) {
    return { bodyUuid: str.slice(0, sep), inertialKey: str.slice(sep + 1) };
  }
  return { bodyUuid: null, inertialKey: str };
}

const _InertialQuickNavSection = React.memo(function InertialQuickNavSection({
  quickNavBackTarget = null,
  onQuickNavBack = null,
  quickNavForwardTarget = null,
  onQuickNavForward = null,
  parentBodyUuid,
  parentBodyName,
  objectLabel,
  onNavigateToObject,
}) {
  const [activeSubmenuKey, setActiveSubmenuKey] = useState(null);
  const setSceneGraphOpen = usePanelStore((s) => s.actions.setSceneGraphOpen);

  const canGoBack = !!quickNavBackTarget && typeof onQuickNavBack === "function";
  const canGoForward = !!quickNavForwardTarget && typeof onQuickNavForward === "function";

  const navigateToParentBody = useCallback(() => {
    if (!parentBodyUuid) return;
    const selection = useSelectionStore.getState();
    selection.setLastSelectionSource("sceneTree");
    if (selection.selectedBody !== parentBodyUuid) {
      selection.selectBody(parentBodyUuid);
    }
    requestInspectorChromeFlash();
  }, [parentBodyUuid]);

  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!target || typeof target.closest !== "function") return false;
      return Boolean(
        target.closest("input, textarea, select, [contenteditable], [role='textbox']")
      );
    };

    const handleAltArrowQuickNav = (event) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === "ArrowLeft" && canGoBack) {
        event.preventDefault();
        event.stopPropagation();
        onQuickNavBack(quickNavBackTarget);
        return;
      }
      if (event.key === "ArrowRight" && canGoForward) {
        event.preventDefault();
        event.stopPropagation();
        onQuickNavForward(quickNavForwardTarget);
        return;
      }
      if (event.key === "ArrowUp" && parentBodyUuid) {
        event.preventDefault();
        event.stopPropagation();
        navigateToParentBody();
      }
    };

    window.addEventListener("keydown", handleAltArrowQuickNav, true);
    return () => window.removeEventListener("keydown", handleAltArrowQuickNav, true);
  }, [
    canGoBack,
    canGoForward,
    navigateToParentBody,
    onQuickNavBack,
    onQuickNavForward,
    parentBodyUuid,
    quickNavBackTarget,
    quickNavForwardTarget,
  ]);

  const backLabel = canGoBack
    ? `Back (ALT + Left): ${quickNavBackTarget.label}`
    : "Back (ALT + Left)";
  const forwardLabel = canGoForward
    ? `Forward (ALT + Right): ${quickNavForwardTarget.label}`
    : "Forward (ALT + Right)";
  const parentBodyLabel = parentBodyUuid
    ? `Parent Body (ALT + Up): ${stripPrefix(parentBodyName) || parentBodyUuid}`
    : "Parent Body (ALT + Up)";
  const objectLabelStripped = objectLabel ? stripPrefix(objectLabel) : null;
  const inspectParentObjectLabel = objectLabelStripped
    ? `Inspect Parent Object: ${objectLabelStripped}`
    : "Inspect Parent Object";
  const canNavigateToObject = !!parentBodyUuid && typeof onNavigateToObject === "function";

  const quickNavActions = [
    {
      key: "back",
      label: backLabel,
      icon: <ArrowLeft className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoBack,
      onDefaultClick: () => {
        if (canGoBack) onQuickNavBack(quickNavBackTarget);
      },
    },
    {
      key: "forward",
      label: forwardLabel,
      icon: <ArrowRight className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoForward,
      onDefaultClick: () => {
        if (canGoForward) onQuickNavForward(quickNavForwardTarget);
      },
    },
    {
      key: "parentBody",
      label: parentBodyLabel,
      icon: <ArrowUp className="w-4 h-4" aria-hidden="true" />,
      disabled: !parentBodyUuid,
      onDefaultClick: navigateToParentBody,
    },
    {
      key: "parentObject",
      label: inspectParentObjectLabel,
      icon: <EntireObjectIcon className="w-4 h-4" aria-hidden="true" />,
      disabled: !canNavigateToObject,
      onDefaultClick: () => {
        if (canNavigateToObject) onNavigateToObject();
      },
    },
  ];

  return (
    <QuickNavSection
      activeSubmenuKey={activeSubmenuKey}
      setActiveSubmenuKey={setActiveSubmenuKey}
      submenuClassName="absolute left-0 top-full mt-1 min-w-[180px] rounded-md border border-gray-600 p-0.5 shadow-xl z-[10061]"
      submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
      actions={quickNavActions}
      headerActions={
        <Tooltip content="Open Scene Graph">
          <button
            type="button"
            aria-label="Open Scene Graph"
            onClick={() => setSceneGraphOpen(true)}
            className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
            style={{ opacity: 0.55 }}
          >
            <Network size={16} aria-hidden="true" />
          </button>
        </Tooltip>
      }
    />
  );
});

const INERTIA_REPRESENTATION_OPTIONS = [
  { value: "diagonal", label: "Diagonal inertia" },
  { value: "full", label: "Full inertia tensor" },
];

const MIN_INERTIA_MOMENT = 1e-9;

const parseFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const sanitizeInertiaComponent = (value, index) => {
  const number = parseFiniteNumber(value, index < 3 ? MIN_INERTIA_MOMENT : 0);
  return index < 3 ? Math.max(number, MIN_INERTIA_MOMENT) : number;
};

const formatInertiaInputValue = (value, decimals = 4) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  const abs = Math.abs(number);
  if (abs > 0 && abs < 10 ** -decimals) return number.toPrecision(4);
  return number.toFixed(decimals);
};

const getInertiaDragSensitivity = (value, basis = 0, rate = 0.02) => {
  const magnitude = Math.max(Math.abs(Number(value) || 0), Math.abs(Number(basis) || 0));
  return Math.max(magnitude * rate, 1e-7);
};

const getInertiaDragSnapStep = (index, value, basis = 0) => {
  const magnitude = Math.max(Math.abs(Number(value) || 0), Math.abs(Number(basis) || 0));
  const minStep = index < 3 ? MIN_INERTIA_MOMENT : 1e-9;
  return Math.max(magnitude * 0.001, minStep);
};

function matchInertialInScene(bodyUuid, inertial) {
  try {
    const { scene } = getViewerHandles();
    if (!scene) return null;
    let found = null;
    scene.traverse((obj) => {
      if (
        !found &&
        obj &&
        obj.userData &&
        obj.userData.type === "inertial" &&
        obj.userData.bodyUuid === bodyUuid &&
        ((obj.userData.inertialUuid &&
          String(obj.userData.inertialUuid) === String(inertial.uuid)) ||
          (obj.userData.inertialName &&
            inertial?.name &&
            obj.userData.inertialName === inertial.name))
      ) {
        found = obj;
      }
    });
    return found;
  } catch {
    return null;
  }
}

function computeInertialScale(diagonal, mass) {
  let largest = 0;
  try {
    const { scene } = getViewerHandles();
    const modelGroup = scene && scene.getObjectByName("modelGroup");
    largest = modelGroup ? computeLargestRobotDimension(modelGroup) : 0;
  } catch {
    // Best-effort viewer-size scaling; fall back to the default factor.
  }
  return computeInertialVisualScale(diagonal, mass, largest);
}

const InertialPropertiesPanel = () => {
  const selectedInertial = useSelectionStore((state) => state.selectedInertial);
  const bodiesRecord = useBodies();
  const worldBodies = useWorldBodies();
  const sceneActions = useSceneActions();
  const _selectionActions = useSelectionStore.getState();
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const { showPositionGizmo, showRotationGizmo, togglePositionGizmo, toggleRotationGizmo } =
    useTransformGizmoControls({
      disabled: transformEditorBlock.disabled,
      enableShortcuts: true,
    });
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();

  const { bodyUuid, inertialKey: _inertialKey } = useMemo(
    () => parseSelectedKey(selectedInertial),
    [selectedInertial]
  );

  const bodyNode = useMemo(
    () => (bodyUuid ? bodiesRecord[bodyUuid] || null : null),
    [bodiesRecord, bodyUuid]
  );
  const inertial = bodyNode?.inertia || null;
  const _objectLabel = useMemo(
    () => getWorldBodyLabelForBodyUuid(bodyUuid, bodiesRecord, worldBodies),
    [bodyUuid, bodiesRecord, worldBodies]
  );
  const { onNavigateToObject: _handleNavigateToObjectProperties } =
    useNavigateToObjectFromBodyUuid(bodyUuid);
  const [mass, setMass] = useState(1.0);
  const [pos, setPos] = useState([0, 0, 0]);
  const [diag, setDiag] = useState([0.1, 0.1, 0.1]);
  const [fullInertia, setFullInertia] = useState(null);
  const [quatDeg, setQuatDeg] = useState([0, 0, 0]);
  const [orientationQuat, setOrientationQuat] = useState([1, 0, 0, 0]);
  const [inertialGlobalPos, setInertialGlobalPos] = useState([0, 0, 0]);

  // Local state for input values while editing (to prevent cursor jumping)
  const [editingInputs, setEditingInputs] = useState({});
  const inertiaRepresentation = fullInertia ? "full" : "diagonal";

  const calculateGlobalInertialPosition = useCallback(() => {
    try {
      const obj = bodyUuid && inertial ? matchInertialInScene(bodyUuid, inertial) : null;
      if (!obj) return [0, 0, 0];
      obj.updateMatrixWorld(true);
      const p = new THREE.Vector3();
      obj.getWorldPosition(p);
      return [p.x, p.y, p.z];
    } catch {
      return [0, 0, 0];
    }
  }, [bodyUuid, inertial]);

  useEffect(() => {
    if (!inertial) return;
    const m = Number.isFinite(inertial.mass) ? inertial.mass : 1.0;
    const p = Array.isArray(inertial.position) ? inertial.position : [0, 0, 0];
    const d = Array.isArray(inertial.diagonal) ? inertial.diagonal : [0.1, 0.1, 0.1];
    const full = Array.isArray(inertial.fullinertia) ? inertial.fullinertia : null;
    const q = Array.isArray(inertial.orientation)
      ? inertial.orientation
      : Array.isArray(inertial.quat)
        ? inertial.quat
        : [1, 0, 0, 0];
    const qw = q[0] ?? 1;
    const qx = q[1] ?? 0;
    const qy = q[2] ?? 0;
    const qz = q[3] ?? 0;
    setMass(m);
    setPos([...p]);
    setDiag([d[0] ?? 0.1, d[1] ?? 0.1, d[2] ?? 0.1]);
    setFullInertia(full ? [...full] : null);
    setOrientationQuat([qw, qx, qy, qz]);
    setQuatDeg(quaternionToEulerDegrees([qw, qx, qy, qz]));
    setInertialGlobalPos(calculateGlobalInertialPosition());
  }, [inertial, bodyUuid, calculateGlobalInertialPosition]);

  if (!selectedInertial || !bodyNode || !inertial) return null;

  const _titleName = inertial.name || String(inertial.uuid) || "inertial";

  const updateInertialObjectShape = (nextInertial, nextMass = mass) => {
    try {
      const obj = matchInertialInScene(bodyUuid, inertial);
      if (!obj) return;
      const principalAxes = resolveInertialPrincipalAxes({
        ...inertial,
        ...nextInertial,
        mass: nextMass,
      });
      if (!principalAxes) return;
      obj.traverse((child) => {
        if (!child || !child.isMesh) return;
        const [sx, sy, sz] = computeInertialScale(principalAxes.diagonal, nextMass);
        child.scale.set(sx, sy, sz);
        child.setRotationFromQuaternion(principalAxes.orientation);
      });
    } catch (error) {
      logger.warn("[InertialPropertiesPanel] Failed to update live inertial scale", error);
    }
  };

  const updateInertialObjectOrientation = (nextQuat) => {
    try {
      const obj = matchInertialInScene(bodyUuid, inertial);
      if (!obj) return;
      const q = new THREE.Quaternion(
        nextQuat[1] || 0,
        nextQuat[2] || 0,
        nextQuat[3] || 0,
        nextQuat[0] || 1
      );
      q.normalize();
      obj.setRotationFromQuaternion(q);
    } catch (error) {
      logger.warn("[InertialPropertiesPanel] Failed to update live inertial orientation", error);
    }
  };

  const handleInertiaRepresentationChange = (nextRepresentation) => {
    if (nextRepresentation === inertiaRepresentation) return;

    if (nextRepresentation === "full") {
      const nextFullInertia = diagonalInertiaToFullInertia(diag, orientationQuat);
      const identityQuat = [1, 0, 0, 0];
      setFullInertia(nextFullInertia);
      setOrientationQuat(identityQuat);
      setQuatDeg([0, 0, 0]);
      sceneActions.updateInertia(bodyUuid, {
        fullinertia: nextFullInertia,
        diagonal: undefined,
        orientation: identityQuat,
      });
      updateInertialObjectOrientation(identityQuat);
      updateInertialObjectShape({ fullinertia: nextFullInertia, diagonal: undefined });
      return;
    }

    const principalAxes = resolveInertialPrincipalAxes({ ...inertial, fullinertia: fullInertia });
    const nextDiagonal = principalAxes?.diagonal || diag;
    const currentQuat = new THREE.Quaternion(
      orientationQuat[1] || 0,
      orientationQuat[2] || 0,
      orientationQuat[3] || 0,
      orientationQuat[0] || 1
    ).normalize();
    const nextOrientation = currentQuat
      .clone()
      .multiply(principalAxes?.orientation || new THREE.Quaternion());
    const nextQuat = [nextOrientation.w, nextOrientation.x, nextOrientation.y, nextOrientation.z];

    setFullInertia(null);
    setDiag(nextDiagonal);
    setOrientationQuat(nextQuat);
    setQuatDeg(quaternionToEulerDegrees(nextQuat));
    sceneActions.updateInertia(bodyUuid, {
      diagonal: nextDiagonal,
      fullinertia: undefined,
      orientation: nextQuat,
    });
    updateInertialObjectOrientation(nextQuat);
    updateInertialObjectShape({ diagonal: nextDiagonal, fullinertia: undefined }, mass);
  };

  const handleMassChange = (value) => {
    const m = parseFloat(value) || 0;
    setMass(m);
    sceneActions.updateInertia(bodyUuid, { mass: m });
    updateInertialObjectShape(
      fullInertia ? { fullinertia: fullInertia, diagonal: undefined } : { diagonal: diag },
      m
    );
    // Update mass range and inertial color based on viewer mass range (blue→red)
    try {
      // Recompute mass range across all inertials in the current bodies
      const masses = [];
      const collect = (bodies) => {
        Object.values(bodies || {}).forEach((b) => {
          if (b && b.inertia && Number.isFinite(b.inertia.mass)) masses.push(b.inertia.mass);
        });
      };
      collect(bodiesRecord);
      if (!Number.isFinite(m) && masses.length === 0) return;
      if (Number.isFinite(m)) masses.push(m);
      const min = masses.length > 0 ? Math.min(...masses) : 0;
      let max = masses.length > 0 ? Math.max(...masses) : 1;
      if (min === max) max = min + 1e-6;
      const viewerActions = getViewerActions();
      if (viewerActions) {
        viewerActions.setInertiaMassRange(min, max);
      }

      const obj = matchInertialInScene(bodyUuid, inertial);
      if (obj) {
        let meshChild = null;
        obj.traverse((child) => {
          if (!meshChild && child && child.isMesh) meshChild = child;
        });
        if (meshChild && meshChild.material) {
          const st = getViewerState();
          const range = st && st.inertiaMassRange ? st.inertiaMassRange : [min, max];
          const rmin = range[0] ?? min;
          const rmax = range[1] ?? max;
          const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
          const t = clamp((m - rmin) / (rmax - rmin), 0, 1);
          const blue = new THREE.Color(0x2196f3);
          const red = new THREE.Color(0xf44336);
          const c = blue.clone().lerp(red, t);
          meshChild.material.color = c;
          meshChild.material.needsUpdate = true;
        }
      }
    } catch (error) {
      logger.warn("[InertialPropertiesPanel] Failed to update live inertial material", error);
    }
  };

  const handlePosChange = (axis, value) => {
    const next = [...pos];
    next[axis] = parseFloat(value) || 0;
    setPos(next);
    sceneActions.updateInertia(bodyUuid, { position: next });
    try {
      const obj = matchInertialInScene(bodyUuid, inertial);
      if (!obj) return;
      obj.position.set(next[0] || 0, next[1] || 0, next[2] || 0);
      setInertialGlobalPos(calculateGlobalInertialPosition());
    } catch (error) {
      logger.warn("[InertialPropertiesPanel] Failed to update live inertial position", error);
    }
  };

  const handleGlobalInertialPosChange = (axis, value) => {
    const nextGlobal = [...inertialGlobalPos];
    nextGlobal[axis] = parseFloat(value) || 0;
    setInertialGlobalPos(nextGlobal);
    try {
      const obj = matchInertialInScene(bodyUuid, inertial);
      if (!obj || !obj.parent) return;
      const parent = obj.parent;
      parent.updateMatrixWorld(true);
      const invParentWorld = new THREE.Matrix4().copy(parent.matrixWorld).invert();
      const worldPos = new THREE.Vector3(nextGlobal[0], nextGlobal[1], nextGlobal[2]);
      const localPos = worldPos.applyMatrix4(invParentWorld);
      const next = [localPos.x, localPos.y, localPos.z];
      setPos(next);
      sceneActions.updateInertia(bodyUuid, { position: next });
      obj.position.set(next[0] || 0, next[1] || 0, next[2] || 0);
    } catch (error) {
      logger.warn("[InertialPropertiesPanel] Failed to update global inertial position", error);
    }
  };

  const handleDiagChange = (axis, value) => {
    const next = [...diag];
    next[axis] = sanitizeInertiaComponent(value, axis);
    setDiag(next);
    sceneActions.updateInertia(bodyUuid, { diagonal: next });
    updateInertialObjectShape({ diagonal: next });
  };

  const handleFullInertiaChange = (axis, value) => {
    const current = fullInertia || [0.1, 0.1, 0.1, 0, 0, 0];
    const next = [...current];
    next[axis] = sanitizeInertiaComponent(value, axis);
    setFullInertia(next);
    sceneActions.updateInertia(bodyUuid, { fullinertia: next });
    updateInertialObjectShape({ fullinertia: next, diagonal: undefined });
  };

  const handleQuatChange = (axis, value) => {
    const nextDeg = [...quatDeg];
    nextDeg[axis] = parseFloat(value) || 0;
    setQuatDeg(nextDeg);
    const quat = eulerDegreesToQuaternion(nextDeg);
    setOrientationQuat(quat);
    sceneActions.updateInertia(bodyUuid, { orientation: quat });
    try {
      const obj = matchInertialInScene(bodyUuid, inertial);
      if (!obj) return;
      const q = new THREE.Quaternion(quat[1] || 0, quat[2] || 0, quat[3] || 0, quat[0] || 1);
      q.normalize();
      obj.setRotationFromQuaternion(q);
    } catch (error) {
      logger.warn("[InertialPropertiesPanel] Failed to update live inertial rotation", error);
    }
  };

  const handleQuaternionChange = (idx, value) => {
    const nextQuat = [...orientationQuat];
    nextQuat[idx] = value;
    setOrientationQuat(nextQuat);
    setQuatDeg(quaternionToEulerDegrees(nextQuat));
    sceneActions.updateInertia(bodyUuid, { orientation: nextQuat });
    try {
      const obj = matchInertialInScene(bodyUuid, inertial);
      if (!obj) return;
      const q = new THREE.Quaternion(
        nextQuat[1] || 0,
        nextQuat[2] || 0,
        nextQuat[3] || 0,
        nextQuat[0] || 1
      );
      q.normalize();
      obj.setRotationFromQuaternion(q);
    } catch (error) {
      logger.warn("[InertialPropertiesPanel] Failed to update live inertial quaternion", error);
    }
  };

  const physicalPropertiesSection = (
    <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
      <Collapsible defaultOpen>
        <CollapsibleTrigger quiet>Physical Properties</CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3">
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <FieldLabel>Mass</FieldLabel>
              </div>
              <DragNumberInput
                label="kg"
                value={
                  editingInputs["mass"] !== undefined
                    ? editingInputs["mass"]
                    : mass?.toFixed(3) || "0"
                }
                dragValue={mass}
                dragDisplayDecimals={3}
                onDragValueChange={(nextValue) => handleMassChange(nextValue)}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditingInputs((prev) => ({ ...prev, mass: val }));
                  const numVal = parseFloat(val);
                  if (!isNaN(numVal)) {
                    handleMassChange(numVal);
                  }
                }}
                onFocus={(e) => {
                  setEditingInputs((prev) => ({ ...prev, mass: e.target.value }));
                }}
                onBlur={(e) => {
                  const numVal = parseFloat(e.target.value) || 0;
                  handleMassChange(numVal);
                  setEditingInputs((prev) => {
                    const next = { ...prev };
                    delete next.mass;
                    return next;
                  });
                }}
                wrapperClassName="flex items-center gap-2"
                labelClassName="text-xs text-gray-300 w-10 text-center"
                inputClassName="min-w-0 flex-1 px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                inputProps={{ step: "0.01" }}
              />
            </div>

            <Separator />

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <FieldLabel className="inline-flex items-center gap-1.5">
                  Gravity Compensation
                  <InfoTooltip
                    text="Fraction of gravity to cancel on this body. 0 = full gravity applies; 1 = fully weightless. Useful for simulating counterbalanced arms or buoyancy."
                    widthClass="w-64"
                  />
                </FieldLabel>
              </div>
              <DragNumberInput
                label="ratio"
                value={
                  editingInputs["gravcomp"] !== undefined
                    ? editingInputs["gravcomp"]
                    : (bodyNode.gravityCompensation ?? 0).toFixed(3)
                }
                dragValue={bodyNode.gravityCompensation ?? 0}
                dragDisplayDecimals={3}
                onDragValueChange={(val) => {
                  const clamped = Math.max(0, Math.min(1, val));
                  sceneActions.setBodyGravityCompensation(bodyUuid, clamped || undefined);
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditingInputs((prev) => ({ ...prev, gravcomp: val }));
                  const numVal = parseFloat(val);
                  if (!isNaN(numVal)) {
                    const clamped = Math.max(0, Math.min(1, numVal));
                    sceneActions.setBodyGravityCompensation(bodyUuid, clamped || undefined);
                  }
                }}
                onFocus={(e) => {
                  setEditingInputs((prev) => ({ ...prev, gravcomp: e.target.value }));
                }}
                onBlur={(e) => {
                  const clamped = Math.max(0, Math.min(1, parseFloat(e.target.value) || 0));
                  sceneActions.setBodyGravityCompensation(bodyUuid, clamped || undefined);
                  setEditingInputs((prev) => {
                    const next = { ...prev };
                    delete next.gravcomp;
                    return next;
                  });
                }}
                disabled={authoringLocked}
                wrapperClassName="flex items-center gap-2"
                labelClassName="text-xs text-gray-300 w-10 text-center"
                inputClassName="min-w-0 flex-1 px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                inputProps={{ step: "0.01", min: "0", max: "1" }}
              />
            </div>

            <Separator />

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <FieldLabel>Inertia Format</FieldLabel>
              </div>
              <MenuSelect
                value={inertiaRepresentation}
                onChange={handleInertiaRepresentationChange}
                options={INERTIA_REPRESENTATION_OPTIONS}
                size="compact"
                aria-label="Inertia format"
              />
            </div>

            <Separator />

            {fullInertia ? (
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <FieldLabel className="inline-flex items-center gap-1.5">
                    Full Inertia Tensor
                    <InfoTooltip
                      text="Full inertia tensor in MuJoCo order: IXX, IYY, IZZ, IXY, IXZ, IYZ. The 3D ellipsoid uses its principal axes for visualization."
                      widthClass="w-72"
                    />
                  </FieldLabel>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {["IXX", "IYY", "IZZ", "IXY", "IXZ", "IYZ"].map((label, index) => {
                    const inputKey = `fullinertia-${index}`;
                    const isEditing = editingInputs[inputKey];
                    const fullInertiaBasis = Math.max(
                      Math.abs(fullInertia[0] || 0),
                      Math.abs(fullInertia[1] || 0),
                      Math.abs(fullInertia[2] || 0)
                    );
                    const displayValue =
                      isEditing !== undefined
                        ? editingInputs[inputKey]
                        : formatInertiaInputValue(fullInertia[index], index < 3 ? 4 : 6);
                    return (
                      <div key={label} className="space-y-1">
                        <DragNumberInput
                          label={label}
                          value={displayValue}
                          dragValue={fullInertia[index]}
                          dragSensitivity={getInertiaDragSensitivity(
                            fullInertia[index],
                            index < 3 ? 0 : fullInertiaBasis,
                            index < 3 ? 0.02 : 0.002
                          )}
                          dragSnapStep={getInertiaDragSnapStep(
                            index,
                            fullInertia[index],
                            index < 3 ? 0 : fullInertiaBasis
                          )}
                          deferDragUpdates={false}
                          stepperPlacement="inline"
                          onDragValueChange={(nextValue) =>
                            handleFullInertiaChange(index, nextValue)
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                            const numVal = parseFloat(val);
                            if (!isNaN(numVal)) {
                              handleFullInertiaChange(index, numVal);
                            }
                          }}
                          onFocus={(e) => {
                            setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
                          }}
                          onBlur={(e) => {
                            const numVal = sanitizeInertiaComponent(e.target.value, index);
                            handleFullInertiaChange(index, numVal);
                            setEditingInputs((prev) => {
                              const next = { ...prev };
                              delete next[inputKey];
                              return next;
                            });
                          }}
                          labelClassName="text-xs text-gray-300 w-7 text-center"
                          inputClassName="w-full py-1 pl-2 pr-8 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                          inputProps={{ step: index < 3 ? "0.0001" : "0.000001" }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <FieldLabel className="inline-flex items-center gap-1.5">
                    Diagonal Inertia
                    <InfoTooltip
                      text="Principal moments of inertia about each body axis (kg·m²). Controls resistance to angular acceleration — larger = harder to spin around that axis. Usually auto-computed from geometry; override only if you have measured values."
                      widthClass="w-72"
                    />
                  </FieldLabel>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {["IXX", "IYY", "IZZ"].map((label, index) => {
                    const inputKey = `diag-${index}`;
                    const isEditing = editingInputs[inputKey];
                    const displayValue =
                      isEditing !== undefined
                        ? editingInputs[inputKey]
                        : formatInertiaInputValue(diag[index], 4);
                    return (
                      <div key={label} className="space-y-1">
                        <DragNumberInput
                          label={label}
                          value={displayValue}
                          dragValue={diag[index]}
                          dragSensitivity={getInertiaDragSensitivity(diag[index])}
                          dragSnapStep={getInertiaDragSnapStep(index, diag[index])}
                          deferDragUpdates={false}
                          stepperPlacement="inline"
                          onDragValueChange={(nextValue) => handleDiagChange(index, nextValue)}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                            const numVal = parseFloat(val);
                            if (!isNaN(numVal)) {
                              handleDiagChange(index, numVal);
                            }
                          }}
                          onFocus={(e) => {
                            setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
                          }}
                          onBlur={(e) => {
                            const numVal = sanitizeInertiaComponent(e.target.value, index);
                            handleDiagChange(index, numVal);
                            setEditingInputs((prev) => {
                              const next = { ...prev };
                              delete next[inputKey];
                              return next;
                            });
                          }}
                          labelClassName="text-xs text-gray-300 w-7 text-center"
                          inputClassName="w-full py-1 pl-2 pr-8 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                          inputProps={{ step: "0.0001" }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </InspectorPhysicsLockedCollapsibleWrap>
  );

  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <InertialVisibilityToolbar inertialUuid={inertial.uuid} bodyUuid={bodyUuid} />
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      <InertialVisibilityToolbar inertialUuid={inertial.uuid} bodyUuid={bodyUuid} />

      <Separator />

      <OffsetEditor
        globalPosition={inertialGlobalPos}
        localPosition={pos}
        localRotationDeg={quatDeg}
        quaternion={orientationQuat}
        onGlobalPositionChange={handleGlobalInertialPosChange}
        onPositionChange={handlePosChange}
        onRotationChange={handleQuatChange}
        onQuaternionChange={handleQuaternionChange}
        defaultOpen
        physicsAuthoringLocked={authoringLocked}
        disabled={transformEditorBlock.disabled}
        disabledTitle={transformEditorBlock.title}
        headerActions={
          <TransformGizmoHeaderActions
            disabled={transformEditorBlock.disabled}
            disabledTitle={transformEditorBlock.title}
            showPositionGizmo={showPositionGizmo}
            showRotationGizmo={showRotationGizmo}
            onTogglePositionGizmo={togglePositionGizmo}
            onToggleRotationGizmo={toggleRotationGizmo}
          />
        }
      />

      <Separator />

      {physicalPropertiesSection}
    </div>
  );
};

export default React.memo(InertialPropertiesPanel);
